<!--

Copyright Paul B. Lucas (C) 2025, 2026   ALL RIGHTS RESERVED.
Licensed to the University of Illinois Urbana Champaign

This component is rendered when user (as an authenticated department approver, supervisor, or system admin) selects the 
VIEW EMPLOYEES route on the initial splash page.  Employees are listed with significant properties in a table row format
if the @media probe returns a screen size > MOBILE.  If MOBILE size is detected, then agreement 'rows' are rendered in  a box 
container with alternating label:value rows in a single column.   Actions are rendered as buttons - three to a column.

Actions available on this component for authorized users (Department and Sys Admins) are  {DELETE (employee)} 

This form is also used to provide the qualifications for remote work data:
    Position is eligible
    Employee's last review is satisfactory
    Employee has adequate on-premises experience


Revisions:
    02/25/2025      pblucas     initial creation
    03/24/2025      pblucas     Added access token check for expiry to graph sequence     


-->
<template>
    <!-- 
        Employees list is based on a call to employee on allowable org codes.
           
        Render switches will render either ViewEditEmployeeEntire on a specific
        employee in list with edit the agreement in list,  or render the entire list on a 
        Boolean render state.  {'LIST', 'VIEW-EDIT'} since only admins enter these pages.
    -->

    <div class="_master_container"> 
        <!-- spin until data if loaded from local file -->
        <div v-if="renderState == 'LIST'">
            <div v-if="!bEmployeesLoaded">
                <p class="_p_patience"> Loading employees from database - be patient!</p>
                <SemipolarSpinner class="_spinner"
                :animation-duration="2000"
                :size="65"
                color="#13294B"/>
            </div>
        </div>

        <div v-if="renderState == 'IMPORTING'">
            <div v-if="!bEmployeesLoaded">
                <p class="_p_patience"> Importing Employees</p>
                <SemipolarSpinner class="_spinner"
                :animation-duration="2000"
                :size="65"
                color="#13294B"/>
            </div>
        </div>

        <div v-if="renderState=='LIST'">
            <div v-if="filteredEmployees?.length  && filteredEmployees.length > 0">
                <div class="_list">
                    <p class="_p_title_header">Employees for {{ orgDisplay }}</p>
                </div>
                <div class="_scrollable_list_container">
                    <div class="_employees_table">
                        <!-- table row for list filters -->
                        <div class="_employees_table_row_fixed" style="top: 0;">
                            <div class="_employees_table_cell">
                                <input class="_input_filter" type="text" v-model="filters['name']" @keyup="setFilters">
                            </div> 
                            <div class="_employees_table_cell">
                                <input class="_input_filter" type="text" v-model="filters['netid']" @keyup="setFilters">
                            </div> 
                            <div class="_employees_table_cell">
                                <input class="_input_filter" type="text" v-model="filters['uin']" @keyup="setFilters">
                            </div> 
                            <div class="_employees_table_cell">
                                <input class="_input_filter" type="text" v-model="filters['home-org']" @keyup="setFilters">
                            </div> 
                            <div class="_employees_table_cell">
                                <input class="_input_filter" type="text" v-model="filters['state']" @keyup="setFilters">
                            </div> 
                            <div class="_employees_table_cell">
                                <button class="_button_clear_filters" @click="clearFilters">Clear Filters</button>
                            </div>
                        </div>

                        <!-- table for for header names -->
                        <div class="_employees_table_row_fixed" style="top: 25px;">
                            <div class="_employees_table_cell"> 
                                <p class="_p_list_header">Name</p>
                            </div> 
                            <div class="_employees_table_cell">
                                <p class="_p_list_header">netid</p>
                            </div>
                            <div class="_employees_table_cell">
                                <p class="_p_list_header">UIN</p>
                            </div>
                            <div class="_employees_table_cell">
                                <p class="_p_list_header">Home Org</p>
                            </div>
                            <div class="_employees_table_cell">
                                <p class="_p_list_header">State of Residence</p>
                            </div>
                            <div class="_employees_table_cell">
                                <p class="_p_list_header">Actions</p>
                            </div>
                        </div>
                    
                        <!-- table row for listed data -->
                        <div class="_employees_table_row" v-for="(_,idx) in filteredEmployees" :key="filteredEmployees[idx].uuid" >
                            <div class="_employees_table_link_cell" style="text-align: left;" 
                                tabindex="0"
                                aria-label="Action link to view/edit employee in list"  
                                aria-describedby="Link on employees name to open view/edit page on employee selected in list row"  
                                @click="viewEditEmployee(filteredEmployees[idx], idx)"
                                @keydown.enter="viewEditEmployee(filteredEmployees[idx],idx)">
                                {{ filteredEmployees[idx]['name-concat'] }}
                            </div>
                            <div class="_employees_table_cell" style="text-align: left;">
                                {{ filteredEmployees[idx]['netid'] }}
                            </div>                
                            <div class="_employees_table_cell">
                                {{ filteredEmployees[idx]['uin'] }}
                            </div>
                            <div class="_employees_table_cell">
                                {{ filteredEmployees[idx]['home-org'] }}
                            </div>
                            <div class="_employees_table_cell">
                                {{ filteredEmployees[idx]['state'] }}
                            </div>
                            <div class="_employees_actions_table_cell">
                                <button class="_action_button" style="order:1;" 
                                    tabindex="0"
                                    aria-label="Action to view or edit employee"  
                                    aria-describedby="Inline button to view/edit employee in list row selected" 
                                    @click="viewEditEmployee(filteredEmployees[idx], idx)"
                                    @keydown.enter="viewEditEmployee(filteredEmployee[idx], idx)">
                                    View/Edit Employee</button>
                                <button class="_action_button_remove" style="order:2;" 
                                    tabindex="0"
                                    aria-label="Delete employee"  
                                    aria-describedby="Inline button to delete employee in list row selected"  
                                    @click="removeEmployee(filteredEmployees[idx], idx)"
                                    @keydown.enter="removeEmployee(filteredEmployee[idx],idx)">
                                    Delete Employee</button>
                            </div>
                        </div>
                    </div>  <!-- end of table -->
                </div>      <!-- end of bordered container-->
            </div>
            <div class="_no_employees_warning_container" v-if="filteredEmployees.length == 0 && bEmployeesLoaded">
                <p class="_employees_warning" style="order: 1;">
                    There are no employees on the provided org list
                </p>
                <div class="_employees_warning_clear" style="order: 2;">
                    <button class="_main_button" 
                        tabindex="0"
                        aria-label="Clears filter list" 
                        aria-decribedby="Allow user option of clearing filters when no results show on current filters" 
                        @click="clearFilters"
                        @keydown.enter="clearFilters">Clear Filters</button>
                </div> 
            </div>
        </div>      <!-- end of list render -->

        <div v-if="renderState=='VIEW-EDIT'">
            <!--  edit the employee selected on idx -->
            <div class="_saved_right" v-if="savedFlag">
                <p class="_p_saved">Employee Saved</p>
            </div> 
            <!-- 
            NOTE: config is passed for global photo suppression.  Other emits call graph for group 
            modification.   'user', 'claims', and 'gat' (graph access token) are used for page
            refresh recovery.  updateEmployee emit sets employee - referenced to props   
            -->
            <ViewEditEmployeeEntire :employee="mutatableEmployee" 
                :key="mutatableEmployee.uuid"
                :employees="filteredEmployees"  
                :user="user" :config="config" :claims="claims" :gat="gat"
                :affiliationCodes="uiucCodes"  
                @setEmplAsSysAdmin="setEmplSysAdminPrivilege"  
                @setEmplAsDeptAdmin="setEmplDeptAdminPrivilege"  
                @addEmplContact="addContactToEmployee" 
                @deleteEmplContact="deleteContactFromEmployee"  
                @setAllowedOrgs="setEmplAllowedOrgs"   
                @setEmplAffiliation="setEmployeeAffiliation"  
                @setOID="setEmployeeOID"                          
                />
        </div>
        <div v-if="renderState == 'IMPORT'">
            <p class="_p_import">
                Because the employee class has array properties, e.g. contacts or allowed orgs, the optimal
                form for imports is to import from JSON.  Employees may be imported from spreadsheet files; however, 
                contact information will will not be available on sourced employee spreadsheets. To 
                import contacts and allowed orgs, import auxilary spreadsheets.  These must have a 'netid' key 
                so the data may be associated with the primary employee record.  Further, you should import the principal
                employee spreadsheet first to ensure that an employee record is available.  If you import an auxilary
                file (contact or allowed-orgs) without a base employee in place, the data will be ignored.
                <br /><br/>
                When importing from XLSX spreadsheets that are formatted in a consolidated format, special characters (+,-)
                in column 1 allow switching to auxiliary properties that are child properties of a specific row. See
                User's Manual <b>TDN 20241005 - Remote Work Application - User Guide-V2.</b>
                <br/><br />

                When an employee is imported, an employee object will be created with an unique UUID and id and 
                stored in the database.  If the employee's netid or uin is already on an employee in the database, 
                then any properties on the import will be used to update the database rather than create a new 
                employee.
                <br/><br/>
                <b>Note: </b>Any spreadsheet column labels must be in the API collection dictionary as class
                properties in order for the values to be processed.  If not, values are skipped and not written 
                to employee object.
                <br/><br/>
                 
                An option is provided to import all employees on the user's allowed orgs from ArchiBus.  Imports from
                ArchiBus will be treated in the same manner, testing first to see whether a record exists with uin 
                and netid, and if no record exists a new employee object will be created and stored.   If a 
                record exists, the existing employee in the database will be updated from ArchiBus data on the
                em record.   ArchiBus properties are auto-translated to corresponding employee
                properties for the API.
            
            </p>
          
            <br />
            <div class="_employees_inline_controls">
                <!-- <button class="_button_export_1"       type="button" @click="selectXLSXFile()">Import from Excel XLSX</button> -->
                <button class="_main_button"       type="button" 
                    tabindex="0"
                    aria-label="Import from Excel CSV" 
                    aria-describedby="Import from Excel CSV"
                    @keydown.enter="selectCSVFile()"   
                    @click="selectCSVFile()">Import from Excel CSV</button>
                <button class="_main_button"       type="button" 
                    tabindex="0"
                    aria-label="Import from Excel XLSX" 
                    aria-describedby="Import from Excel XLSX"
                    @keydown.enter="selectXLSXFile()"   
                    @click="selectXLSXFile()">Import from Excel XLSX</button>
                <button class="_main_button"       type="button" 
                    tabindex="0"
                    aria-label="Inport from JSON file" 
                    aria-describedby="Import from JSON"
                    @keydown.enter="selectJSONFile()"   
                    @click="selectJSONFile()">Import from JSON</button>
                <button class="_main_button"       type="button" 
                    tabindex="0"
                    aria-label="Import  employees from Archibus" 
                    aria-describedby="Import  employees "
                    @keydown.enter="importEmTable()"
                    @click="importEmTable()">Import from ArchiBus</button>
                <button class="_main_button"       type="button" 
                    tabindex="0"
                    aria-label="Cancel exports and return to list" 
                    aria-describedby="Cancels export"
                    @click="renderState = 'LIST'"  
                    @keydown.enter="renderState = 'LIST'">Cancel</button>  
           </div>
        </div>
        <div v-if="renderState == 'FILE'">
            <div v-if="processSelector != 'JSON'" class="_import_fieldset">     
                <div class="_row" style="order:1">
                    <div class="_col_30">
                        <p class="_p_import_right">
                            Select the type of import:
                        </p>
                    </div>
                    <div class="_col_70">
                        <div class="_scol_25">
                            <div class="_scol_15">
                                <input id="_import_type_principal" tabindex="0"
                                    aria-label="Employee principal import" 
                                    aria-describedby="Button to select principal employee record import CSV" 
                                    type="radio" name="import_radio" class="_radio" v-model="employeeImportType" value="employees">
                            </div>
                            <div class="_scol_80">
                                <label class="_item_label">Employee</label>
                            </div>
                        </div>
                        <div class="_scol_25">
                            <div class="_scol_15">
                                <input id="_import_type_schedule" tabindex="0"
                                    aria-label="Employee contact import" 
                                    aria-describedby="Button to select auxilary contacts CSV" 
                                    type="radio" name="import_radio" class="_radio" v-model="employeeImportType" value="contacts">
                            </div>
                            <div class="_scol_80">
                                <label class="_item_label">Contacts</label>
                            </div>
                        </div>
                        <div class="_scol_25">
                            <div class="_scol_15">
                                <input id="_import_type_asset" tabindex="0"
                                    aria-label="Employee allowed orgs import" 
                                    aria-describedby="Button to select auxiliary allowed orgs CSV" 
                                    type="radio" name="import_radio" class="_radio" v-model="employeeImportType" value="allowed-orgs">
                            </div>
                            <div class="_scol_80">
                                <label class="_item_label">Allowed Orgs</label>
                            </div>
                        </div>
                        <div class="_scol_25">
                            <div class="_scol_15">
                                <input id="_import_type_asset" tabindex="0"
                                    aria-label="Employee allowed orgs import" 
                                    aria-describedby="import_radio" 
                                    type="radio" name="import_radio" class="_radio" v-model="employeeImportType" value="consolidated">
                            </div>
                            <div class="_scol_80">
                                <label class="_item_label">Allowed Orgs</label>
                            </div>
                        </div>
                    </div>
                 </div>   
                 <div class="_row" style="order:2">
                    <p class="_p_import_center" v-if="employeeImportType=='employees'"> 
                        You have selected an {{employeeImportType}} file to import.  This selection will import only principal 
                        employee data and <b>no</b> contacts or allowed orgs.
                    </p>
                    <p class="_p_import_center" v-if="employeeImportType=='contacts' || employeeImportType=='allowed-orgs'"> 
                        You have selected <span v-if="agreeImportType=='assets'">an</span><span v-else>a</span> {{employeeImportType}} auxilary file to import.  In order to
                        associate the {{ employeeImportType }} records with an employee, you must include
                        in the data row either an employee netid or employee UUID to act as a key.
                    </p>
                    <p class="_p_import_center" v-if="employeeImportType=='consolidated'"> 
                        You have selected a {{employeeImportType}} file to import.  This file should 
                        be structured as rows of employees with interspersed contacts and/or allowed orgs
                        where the contact or allowed org records are added using the +/- syntax.  Refer to 
                        the user manual for details.
                    </p>
         
                </div>
                <div class="_row" style="order:3">
                    <p class="_p_import_center"> 
                    Select a file to import. Type is {{ processSelector }}.
                    </p>
                    <div>
                        <input id="file_01" class="_file_selector" type="file" tabindex="-1" 
                            aria-label="File selection for import" 
                            aria-describedby="File to import in CSV or JSON format" 
                            accept=".csv,.xls,.xlsx,text/csv, application/json, application/vnd: ms-excel"  
                            @change="importSelection"/>
                    </div>
                </div>
            </div>    
        </div>

        <!--   insert  -->
        <div class="_export_container" v-if="renderState == 'EXPORT'">
            <p class="_p_export" style="order: 1;">
                The employee class has array properties, e.g. contacts.  Because spreadsheets are 
                flat in nature, an EXCEL export will not normally include contacts.  If employee 
                contacts are required, click the 'include array properties checkbox'.  Then select the 
                desired format.  'Consolidated' will generate in-table contact sub-tables in the 
                employee record.  If 'individual' is selected, a separate worksheet ("tab") will be created
                in the workbook.  The employee's netid will be added to each contact row acting as a foreign 
                attribute so the contact may be associated with the parent. <br /><br />
                
                JSON files will include all subordinate array properties.                <br /><br/>
            </p>
            <div class="_export_check" >
                <div class="_export_rules" style="order:1;">
                    <input id="export_prop1" class="_check" type="checkbox" v-model="bIncludeArrayProps" 
                        tabindex="0"
                        aria-label="Checkbox to include subordinate properties in EXCEL workbook" 
                        aria-describedby="When checked, includes additional tabs with array properties" 
                        value=true>
                    <label class="_check_label" for="export_prop1">Include all array properties (e.g. schedules or assets)</label>
                </div>
                <div class="_export_rules" style="order: 2;" v-if="bIncludeArrayProps">
                    <label class="_input_label" for="format_type">Select export formatting option</label>
                    <select id="format_type" class="_select" 
                        v-model="sExportFormat">
                        <option id="format_list" class="_type_list" v-for="(item) in aExportFormats" 
                            aria-label="Subordinate properties formatting options" 
                            aria-description="Dropdown list to select format option"
                            :value="item"> {{item}} 
                        </option>
                    </select>
                </div>
            </div>
            <br />
            <div class="_employees_inline_controls" style="order: 2;">
                <!-- button class="_button_export_1"       type="button" @click="exportXLSX()">Save as Excel XLSX</button> -->
                <button class="_main_button" type="button" 
                    tabindex="0" 
                    aria-label="Save as Excel file" 
                    aria-describedby="Selects Excel file type for export" 
                    @click="exportXLSX()" 
                    @keydown.enter="exportXLSX()">Save as Excel</button>
                <button class="_main_button"       type="button" 
                    tabindex="0"
                    aria-label="Save as JSON file" 
                    aria-describedby="Selects JSON file type for export"
                    @click="exportJSON()"
                    @keydown.enter="exportJSON()">Save as JSON</button>  
                <button class="_main_button"       type="button" 
                    tabindex="0"
                    aria-label="Cancel exports and return to list" 
                    aria-describedby="Cancels export"
                    @click="renderState = 'LIST'"  
                    @keydown.enter="renderState = 'LIST'">Cancel</button>  
            </div> 
        </div>

        <div class="_employees_controls">
            <div v-if="filteredEmployees?.[viewIndex] && renderState=='VIEW-EDIT'" style="order: 1;">
                <!--  edit the employee selected on idx -->
                <div class="_saved_center" v-if="savedFlag">
                    <p class="_p_saved">Employee Saved</p>
                </div> 
            </div>
            <div v-else>
                <br />
            </div>
            <div class="_employees_inline_controls" v-if="renderState == 'VIEW-EDIT'" style="order: 2;">
                <button class="_main_button"  type="button" @click="saveEmployee">Save changes</button> 
                <button class="_main_button"  type="button" @click="cancelEmployeeEdits">Cancel </button>
                <button class="_main_button"  type="button" @click="backToList()">Back to List</button> 
                <button class="_main_button"   type="button" 
                    tabindex="0"
                    aria-label="Generate PDF of agreement" 
                    aria-describedby="Generate PDF"
                    @click="generatePDF()"
                    @keydown.enter="generatePDF()">Create a PDF</button>

                <button class="_main_button" type="button" v-if="(employees.length > 1)"
                    @click="nextEmployee()">Next </button> 
            </div>
 
            <div class="_employees_inline_controls" v-if="renderState == 'LIST'" style="order: 2;">

                <button class="_main_button" type="button" @click="importEmployees()">Import Employees </button>
                <button class="_main_button" type="button" @click="exportEmployees()">Export Employees</button>    
            </div>
            <!-- 
            <span v-if="checkAdmin(agreement)">
                 <button class="_main_button" type="button" @click="deleteEmployee">Delete Employee </button>
            </span>
        -->
        </div>
        <!--   breaks to allow footer space -->
        <br/><br/>
    </div>
    
</template>

<script>
    import {ref, computed}              from 'vue'
    import {useStore}                   from 'vuex'
    
    import ViewEditEmployeeEntire       from '@/components/Employees/ViewEditEmployeeEntire.vue'
    import { SemipolarSpinner }         from 'epic-spinners'
 
    // import composables
    import useEmployeesAPI              from '@/composables/useEmployeesAPI'
    import useArchiBusAPI               from '@/composables/useArchiBusAPI'
    import useAgreementsAPI             from '@/composables/useAgreementsAPI'
    import useAssetsAPI                 from '@/composables/useAssetsAPI'
    import useRefreshAPI                from '@/composables/useRefreshAPI'
    import usePermissionsAPI            from '@/composables/usePermissionsAPI'
    import useGraphAPI                  from '@/composables/useGraphAPI'
    import useAuthAPI                   from '@/composables/useAuthAPI'

    // import enumerations
    import SysConfigs                   from '@/configuration/form-options.json'
    import AdministrativeGroups         from '@/configuration/adminGroups.config.json'
    import * as XLSX                    from 'xlsx-js-style'   

    // pdf converter  install as npm install --save dom-to-pdf
    import domToPdf                     from 'dom-to-pdf'

    export default {
        components: {
            ViewEditEmployeeEntire,
            SemipolarSpinner
        },
        props: [
            'claims',
            'gat'
        ],

        emits: [
        ],
    
        setup(props)    {
            
            const { emplResults,  fetchEmployeesOnOrgs, fetchEmployeesOnQuery }    =   useEmployeesAPI()           
            const store                                     =   useStore()
            const { checkStoreForRefresh }                  =   useRefreshAPI()
            const { checkAdminGroups }                      =   usePermissionsAPI() 
            const { uiucCodeResults, makeUIUCCodes }        =   useArchiBusAPI()
 
            //  store data
            const user                  =   computed( () => store.state.user)
            const config                =   computed( () => store.state.config)
            const systemAdministrator   =   computed( () => store.state.user['is-sys-admin'])
            const deptAdministrator     =   computed( () => store.state.user['is-dept-admin'])

            //  actions on store
            const setUser               = (u)    => store.dispatch('setUser', u)

            const sysConfigs            =   ref([])
            sysConfigs.value            =   SysConfigs
            const AdminGroups           =   ref([])
            AdminGroups.value           =   AdministrativeGroups['securityGroups']

            // for UIUC coding system
            const uiucCodes             =   ref({})
            const bSortByName           =   ref(false)   // true sorts listed by 'name' | false by 'code';
            const aCollegeCodes         =   ref([])      // name[0], college-code[1]
            const aDeptCodes            =   ref([])      // name[0], college-code[1], dept-code[2]
            const aOrgCodes             =   ref([])      // name[0], dept-code[1], org[2]



            const employees             =   ref([])
            const bEmployeesLoaded      =   ref(false)   
          
            const backupEmployee        =   ref({})                     // for cancel operations
            const mutatableEmployee     =   ref({})                     // for passing to components
            const aExportableEmployees  =   ref([])
            const bannerPersons         =   ref([])
            const storableEmployees     =   ref([])                     // for imports

            const viewIndex             =   ref(0)   
            const orgDisplay            =   ref(null)
            const processSelector       =   ref(null)
            const selectedFilename      =   ref("")

            const filters               =   ref({
                'name':             "",
                'netid':            "",
                'uin':              "",
                "home-org":         "",
                "state":            "",
            })

            // generate code set
            makeUIUCCodes()
            .then( () => {
                uiucCodes.value = uiucCodeResults.value

                // select list 
                aCollegeCodes.value         =   uiucCodes.value['college-codes']
                aDeptCodes.value            =   uiucCodes.value["dept-codes"]             // name[0], college-code[1], dept-code[2]
                aOrgCodes.value             =   uiucCodes.value["org-codes"]    

            })

            const aOrgs   =   ref(Array.prototype.slice.call(user.value['allowed-orgs']))
            //console.log("LE:: Setup orgs argument on call: "+JSON.stringify(aOrgs.value))
            orgDisplay.value = user.value['allowed-orgs'].toString()

          
            // MARK:  refresh store from database if global browser refresh fubars the store
            checkStoreForRefresh()
            .then( async () => {
                if(user.value['is-sys-admin']) {
                     await fetchEmployeesOnQuery({})        
                    .then( () => {
                         employees.value = emplResults.value 
                         bEmployeesLoaded.value = true
                    })
                }
                else { 
                    await fetchEmployeesOnOrgs(aOrgs.value)        
                    .then( () => {
                        employees.value = emplResults.value 
                        bEmployeesLoaded.value = true
                    })
                }
                
            })
            checkAdminGroups(props.claims)

            const filteredEmployees = computed( () => {

                let aFilteredValues = employees.value

                aFilteredValues = aFilteredValues.filter( (empl) => {
                    return empl['name-concat'].toLowerCase().match(filters.value['name'].toLowerCase())
                })
                aFilteredValues = aFilteredValues.filter( (empl) =>  {
                    return empl['netid'].toLowerCase().match(filters.value['netid'].toLowerCase())
                })
                aFilteredValues = aFilteredValues.filter( (empl) =>  {
                    return empl['uin'].match(filters.value['uin'])
                })
                aFilteredValues = aFilteredValues.filter( (empl) =>  {
                    return empl['home-org'].match(filters.value['home-org'])
                })
                aFilteredValues = aFilteredValues.filter( (empl) =>  {
                    return empl['state'].toLowerCase().match(filters.value['state'].toLowerCase())
                })
                return aFilteredValues

            })

            return {user, config, systemAdministrator, deptAdministrator, employees, bEmployeesLoaded, 
                    backupEmployee, filteredEmployees, filters, sysConfigs, viewIndex, orgDisplay, 
                    aExportableEmployees, bannerPersons, processSelector, selectedFilename, setUser, AdminGroups,
                    mutatableEmployee, aOrgs, uiucCodes, bSortByName, aCollegeCodes,
                    aDeptCodes, aOrgCodes, storableEmployees }
        },

        data() {
 
            return {
                // switch for page conditional rendering        
                renderState:        'LIST',
                savedFlag:          false,
                filepath:           "",
                filename:           "",
                datafile:           "",
                bIncludeArrayProps: false,
                aExportFormats:  [
                    "consolidated",
                    "individual"
                ],
                sExportFormat:   "consolidated",
                employeeImportType: "employees"
            }
        },
        methods: {
        
            //  >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
            //  MARK:  - THE FOLLOWING ARE ACTION METHODS ASSOCIATED WITH THE LISTED ACTION BUTTONS FOR A GENERAL USER
            //  >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>

            // FUNCTION: takes the employee on the list using the v-for indices and renders entire form
            // Function is member of action list with (employee, idx) argument
            viewEditEmployee(_, idx) {
                this.viewIndex = idx

                // generate the deep copy mutatable versions of the selection
                this.mutatableEmployee      =   JSON.parse(JSON.stringify(this.filteredEmployees[idx]))
                // set agreement and copy (non-reactive) for backup operation
                this.backupEmployee         =   JSON.parse(JSON.stringify(this.filteredEmployees[idx]))
                this.renderState            =   "VIEW-EDIT"
            },
            //   FUNCTION: to delete Employee from database
            removeEmployee(_, idx) {
                const { emplKeyData, emplResults, deleteEmployeesOnQuery, fetchEmployeesOnQuery, fetchEmployeesOnOrgs}   = useEmployeesAPI()        
                const { agreeResults, deleteAgreementsOnQuery } = useAgreementsAPI()
                const { assetResults, deleteAssetsOnQuery } = useAssetsAPI()
                if(this.filteredEmployees[idx].uuid == this.user.uuid) {
                    console.log("ListEmployees:: removeEmployee: User trying to remove self.  Disallowed ")
                    return
                }
                // cannot delete on mutableEmployee as this is set on ViewEdit and thus not defined.
                // fetch the employee to obtain the id - uuid is unique
                fetchEmployeesOnQuery({'uuid': this.filteredEmployees[idx].uuid})
                .then( async () => {
                    let emplId = emplResults.value[0].id
                    await deleteEmployeesOnQuery({"uuid": this.filteredEmployees[idx].uuid})
                    .then( async () => {
                        if (emplKeyData.value.status == 'failed') {
                            console.log("LE:: removeEmployee: server responds failed: msg: "+ emplKeyData.value.error)
                        }
                        else {
                            // reload employees
                            await fetchEmployeesOnOrgs(this.aOrgs)
                            .then( () => {
                                this.employees = emplResults.value 
                            })
                            .catch( error =>  {
                                console.log("LE:: removeEmployee: refetch failed: "+error.message)
                            })
                        }
                    })
                    .catch( error =>  {
                        console.log("LE:: removeEmployee: Failed: "+error.message)
                    })
                    return emplId
                })
                .then( async(emplId)  =>  {
                    // delete all agreements associated with the employee
                    await deleteAgreementsOnQuery( {'fk_employee_id': emplId})

                    // delete all assets with employee as steward
                    await deleteAssetsOnQuery( {'fk_employee_id': emplId})
                })                
                
            },
          
            //  >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
            //  MARK:  THE FOLLOWING METHODS ARE AVAILABLE AS POSSIBLE ACTION POST EDIT OF AN AGREEMENT
            //  >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>

            // FUNCTION: Disregards any edits and retores the agreement to a new agreeement (PENDING) state
            //
            cancelEmployeeEdits()   {
                // use mutatedEmployee as this is called on edit page in parent
                // return form to pristine state and disregard edits
                this.mutatedEmployee    =   this.backupEmployee 
                // to change behavior from just resetting form to return to list
                // reverse commenting
                //this.viewIndex          =   0
                //this.renderState        =   "LIST"
                this.renderState            =   "VIEW-EDIT"
            },
           
            // FUNCTION - Saves an employee edited on ViewEditForm 
            // 
            async saveEmployee()   {              
                const { emplResults, emplKeyData, fetchEmployeesOnQuery, storeEmployee}       = useEmployeesAPI()           
                const { checkAdminGroups }      =   usePermissionsAPI() 

                await storeEmployee(this.mutatableEmployee)
                .then( async () => {
                    await fetchEmployeesOnQuery({id: emplKeyData.value.results[0].pkey})
                    .then( () => {
                        //this.filteredEmployees[this.viewIndex] is entry in table
                        this.filteredEmployees[this.viewIndex] = emplResults.value[0]  
 
                        // if this employee is also the current user, update the store
                        if (emplResults.value[0].netid == this.user.netid) {
                            this.setUser(emplResults.value[0])

                            // do the groups and permissions
                            checkAdminGroups(this.claims)
                        }
                        this.renderState        =   "VIEW-EDIT"
                        this.savedFlag          =   true  

                        // rebuild mutatableEmployee
                        this.mutatableEmployee = JSON.parse(JSON.stringify(emplResults.value[0]))
                    
                    })
                })    
            },   


             // MARK:  THE FOLLOWING METHODS ARE APPLIED TO VIEW/EDIT ELEMENTS TO RETURN TO OVERALL LIST - GENERAL USERS
            
            backToList() {

                this.renderState    =   "LIST"
                this.savedFlag      =   false
                /*
                const { emplResults, emplKeyData, fetchEmployeesOnQuery,fetchEmployeesOnOrgs, storeEmployee}       = useEmployeesAPI()           

                // need to reload list as there may be new copies
                fetchEmployeesOnOrgs({'employee-fk': this.user.id})
                .then( () => {
                    this.employees      =   emplResults.value 
                    this.renderState    =   "LIST"
                }) 
                */  
            },
            // FUNCTION: increments through viewing index (experimental)
            nextEmployee() {
                this.viewIndex++
                if (this.viewIndex >= this.filteredEmployees.length) this.viewIndex = 0

                // generate the deep copy mutatable versions of the selection
                this.mutatableEmployee      =   JSON.parse(JSON.stringify(this.filteredEmployees[this.viewIndex]))
                // set agreement and copy (non-reactive) for backup operation
                this.backupEmployee         =   JSON.parse(JSON.stringify(this.filteredEmployees[this.viewIndex]))

            },
            // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
            // Data Import Processes
            // Button triggers render switch to IMPORT rendering
            // Option buttons are presented
            // User clicking option button will requets file name for files or go immediately to API for Banner
            // For file imports, a file type state is set and the 'FILE' page is rendered.
            // Specified file is accessed and processing in switched on processSelector 
            // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>> 

            importEmployees()   {
                this.renderState = 'IMPORT'
            },
            // Function is triggered on file name submit on rendered page
            importSelection(event) {
                this.selectedFilename = event.target.value

                switch(this.processSelector) {
                    
                    case "JSON":
                        this.processorSelector = null
                        const JSONFileInput = document.querySelector("input[type=file]")
                        JSONFileInput.addEventListener("change", this.processJSONImport(JSONFileInput))
                        break;

                    case "CSV":
                        this.processorSelector = null
                        const CSVFileInput = document.querySelector("input[type=file]")
                        CSVFileInput.addEventListener("change", this.processCSVImport(CSVFileInput))
                        break;

                    case "XLSX":
                        this.processorSelector = null
                        const CSLSXFileInput = document.querySelector("input[type=file]")
                        CSLSXFileInput.addEventListener("change", this.processXLSXImport(CSLSXFileInput))
                        break;

                    default:
                        break;
                }
            },
         
            selectJSONFile() {
                this.processSelector = 'JSON'
                this.renderState = 'FILE'
            },
            selectCSVFile()    {
                this.processSelector = 'CSV'
                this.renderState = 'FILE'
            },
            selectXLSXFile() {
                this.processSelector = 'XLSX'
                this.renderState = 'FILE'
            },
            async processJSONImport(fileInput) {

                const { emplResults, emplKeyData,fetchEmployeesOnOrgs, storeEmployee } = useEmployeesAPI()

                const [file] = fileInput.files
                var rawJsonData = ""
                if (file) {
                    rawJsonData = await file.text()
                    const aJsonData = JSON.parse(rawJsonData)
                    let resolvedPromises = await Promise.allSettled(aJsonData.map(oEmpl => {
                            storeEmployee(oEmpl)
                            return emplKeyData.value.results[0]
                        }
                    ))
                    // have an array of settled promised with either resolve or response callbacks
                    resolvedPromises.forEach((rp, _)  =>  {
                        if(rp.status == 'rejected') {
                            //throw Error ("Axios promise rejected: fetching orgcode: "+oEmpl[num])
                        }
                        else if(rp.status == 'fulfilled')  {
                            // data available on promise
                            const keyData = rp.value.data      
                            
                            // do some more detailed error checking on API return
                            // data is JSON
                            if(!keyData.message.includes('success')) { 
                                console.log("StoreEmployees:: error thrown in storing employees")
                            }
                            // completed and valid query returns 
                        }
                    })
                    .then( () =>    {
                        // refetch employees just loaded for list
                        // loaded employees may have disparate orgs but can see only allowed orgs
                        fetchEmployeesOnOrgs([this.aOrgs])
                        return emplResults.value
                    })
                    .then( (fetchedEmpls)   =>  {
                        this.employees = fetchedEmpls
                        this.renderState = 'LIST'
                    })
                }   
            },
            // May be importing different spreadsheets based on employeeImportType which can be
            // employees | contacts | allowed-orgs
            // Loading employees is straight forward.   Import CSV, generate employee object and store on Promise.all.
            // For contacts, CSV must have netid key.  Key is extracted and remaining row data used as contact attributes
            // For each mutator which consists of a contact array and netid, the employee is fetched on netid, UUID extracted,
            // and employee object created using UUID and contacts array.  This will 'update on dupicate key' the database
            // record.
            // Allowed orgs is handled the same way
            //
            async processCSVImport(fileInput)  {

                const { emplResults, emplKeyData, fetchEmployeesOnQuery, 
                        fetchEmployeesOnOrgs, storeEmployee }               = useEmployeesAPI()

                const [file] = fileInput.files
                var rawData = ""
                const loadableObjects = []

                if (file) {
                    rawData = await file.text()
                    const CSVData = rawData.replaceAll('\r','').split('\n') // split by lines

                    // each row of CSVData is comma delimited set of attributes
                    // first row is array keys - these must be translatable into API employee dictionary keys
                    // this is true for all components of employeeImportType
                    // NOTE: allowed orgs is simple:  {netid, org} as simple array  One value per row
                    //
                    const objKeys = CSVData[0].split(',')
                    
                    for( let i=1; i < CSVData.length-1; i++) {
                        const aObjAttributeSet = {}                // mutator object
                        var aAttributeSet = CSVData[i].split(',')
                        // iterate the attributes on each line converting them into a key: value set for store  
                        for (let j=0; j < aAttributeSet.length; j++) {
                            let key = objKeys[j]
                            aObjAttributeSet[key] = aAttributeSet[j]
                        }
                        // object constructed. Add to loadables
                        loadableObjects.push(aObjAttributeSet)
                    }

                    //process the loadable object (per import type)
                    switch( this.employeeImportType)    {

                        case 'employees':
                            let resolvedPromises = await Promise.allSettled(loadableObjects.map( oEmpl => {
                                storeEmployee(oEmpl)
                                .then( () => {
                                    return emplKeyData.value.results[0]
                                })     
                            }))
                            // have an array of settled promised with either resolve or response callbacks
                            resolvedPromises.forEach((rp, _)  =>  {
                                if(rp.status == 'rejected') {
                                    //throw Error 
                                }
                                else if(rp.status == 'fulfilled')  {
                                    // data available on promise
                                    const keyData = rp.value.data      
                                    
                                    // do some more detailed error checking on API return
                                    // data is JSON
                                    if(!keyData.message.includes('success')) { 
                                        console.log("StoreEmployees:: error thrown in storing employees")
                                    }
                                    // completed and valid query returns 
                                }
                            })
                            .then( () =>    {
                                // refetch employees just loaded for list
                                // loaded employees may have disparate orgs but can see only allowed orgs
                                fetchEmployeesOnOrgs([this.aOrgs])
                                .then( () => {
                                    return emplResults.value
                                })  
                            })
                            .then( (fetchedEmpls)   =>  {
                                this.employees = fetchedEmpls
                                this.renderState = 'LIST'
                            })
                            break

                        case 'contacts':
                            // imports contacts from CSV and creates set objects, e.g. each contact will have a
                            // key of the netid and some contat attributes - one row per contact.  There may be multiple rows. 
                            //
                            //  Note: store will overwrite old contacts. Be sure to include any existing contacts
                            //
                            let aContactMutators    =   []          // array of mutator objects for all listed employees
                            loadableObjects.forEach( (o) => {
                                let mutator =  {                    // each employee will have a mutator
                                    "netid":    "",
                                    "contacts": []
                                }
                                let idx = aContactMutators.findIndex( (cmo) => {
                                    cmo.netid === o.netid
                                }) 
                                if(idx == -1) {
                                    // netid is not in list of mutators so add it and the contact attributes
                                    mutator.netid = o.netid
                                    mutator.contacts.push(delete o.netid)   // delete netid and push the rest
                                    aContactMutators.push(mutator)
                                }
                                else {
                                    // mutator with netid exists so just add a contact
                                    aContactMutators[idx].contacts.push(delete o.netid)

                                }
                            })
                            // result is a set of contact mutators - identify stored employee and update contacts
                            // Form:  [
                            //          { netid: xx1, contacts: []},
                            //          { netid: xx2, contacts: []}
                            //        ]
                            //
                            let resolvedContactPromises = await Promise.allSettled(aContactMutators.map( mutator => {
                                fetchEmployeesOnQuery({'netid': mutator.netid}) 
                                .then ( () => {
                                    return {
                                        // have to do this because unique key is uuid not netid
                                        "uuid":emplResults.value[0].uuid,   // netid makes return unique
                                        "contacts": mutator.contacts
                                    }    
                                })      
                            }))
                            // have an array of settled promises with either resolve or response callbacks
                            resolvedContactPromises.forEach((rp, _)  =>  {
                                if(rp.status == 'rejected') {
                                    //throw Error ()
                                }
                                else if(rp.status == 'fulfilled')  {
                                    // data available on promise
                                    const empl = rp.value.data      
                                    storeEmployee(empl)
                                    .then( () => {
                                        return emplKeyData.value.results[0]
                                    })  
                                }   
                            })
                            .then( () =>    {
                                // refetch employees just loaded for list
                                // loaded employees may have disparate orgs but can see only allowed orgs
                                fetchEmployeesOnOrgs([this.aOrgs])
                                .then( () => {
                                    return emplResults.value
                                })     
                            })
                            .then( (fetchedEmpls)   =>  {
                                this.employees = fetchedEmpls
                                this.renderState = 'LIST'
                            })
                            break

                        case 'allowed-orgs':
                            // imports contacts from CSV and creates set objects, e.g. each contact will have a
                            // key of the netid and some contat attributes - one row per contact.  There may be multiple rows. 
                            //
                            //  Note: store will overwrite old contacts. Be sure to include any existing contacts
                            //
                            let aOrgMutators    =   []          // array of mutator objects for all listed employees
                            loadableObjects.forEach( (o) => {
                                let mutator =  {                    // each employee will have a mutator
                                    "netid":    "",
                                    "orgs": []
                                }
                                let idx = aContactMutators.findIndex( (cmo) => {
                                    cmo.netid === o.netid
                                }) 
                                if(idx == -1) {
                                    // netid is not in list of mutators so add it and the contact attributes
                                    mutator.netid = o.netid
                                    mutator.orgs.push(delete o.netid)   // delete netid and push the rest
                                    aOrgMutators.push(mutator)
                                }
                                else {
                                    // mutator with netid exists so just add a contact
                                    aOrgMutators[idx].orgs.push(delete o.netid)

                                }
                            })
                            // result is a set of contact mutators - identify stored employee and update contacts
                            // Form:  [
                            //          { netid: xx1, orgs: []},
                            //          { netid: xx2, orgs: []}
                            //        ]
                            //
                            let resolvedOrgPromises = await Promise.allSettled(aOrgMutators.map( mutator => {
                                fetchEmployeesOnQuery({'netid': mutator.netid}) 
                                .then ( () => {
                                    return {
                                        // have to do this because unique key is uuid not netid
                                        "uuid":emplResults.value[0].uuid,   // netid makes return unique
                                        "orgs": mutator.orgs
                                    }    
                                })      
                            }))
                            // have an array of settled promises with either resolve or response callbacks
                            resolvedOrgPromises.forEach((rp, _)  =>  {
                                if(rp.status == 'rejected') {
                                    //throw Error ()
                                }
                                else if(rp.status == 'fulfilled')  {
                                    // data available on promise
                                    const empl = rp.value.data      
                                    storeEmployee(empl)
                                    .then( () => {
                                        return emplKeyData.value.results[0]
                                    })  
                                }   
                            })
                            .then( () =>    {
                                // refetch employees just loaded for list
                                // loaded employees may have disparate orgs but can see only allowed orgs
                                fetchEmployeesOnOrgs([this.aOrgs])
                                .then( () => {
                                    return emplResults.value
                                })     
                            })
                            .then( (fetchedEmpls)   =>  {
                                this.employees = fetchedEmpls
                                this.renderState = 'LIST'
                            })
                            break  
                        
                        case 'consolidated':

                            break
                    }
                    
                }
                // back to the barn
                this.renderState = 'LIST'
                //this.router.push({name: 'listview'})
            },

            // FUNCTION: Import from Banner
            // Person objects are imported on org codes (allowed-orgs on user)
            // Returned list of persons are iterated and checked against database on netid and UIN
            // If not in database a new object is created and stored
            // If in database, the UUID is recovered and added to attributes, then the attribute list
            // is filtered to remove empty attributes - then
            // an employee record is inserted where UPDATE ON DUPLICATE KEY will update the 
            // existing record.
            //
            // DEVELOPER:: MARK: !! Defer until fetch on Orgs developed by AITS
            // Currently only fetches on UIN
            importBanner() {
                console.log("LE:: importBanner triggered")
                /*
                const {personResults, fetchPersonsOnOrgs} = useBannerAPI()

                fetchPersonsOnOrgs(this.aOrgs)
                .then( () => {

                    this.bannerPersons = personResults.value

                })
                */
                // back to the barn
                this.renderState = 'LIST'
            },
            // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
            //
            //      Import from ArchiBUs EM table
            //      Imports on allowed orgs
            //
            // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
            async importEmTable() {
                const { employeeEMResults, fetchEMEmployeesOnOrgs, makeUIUCCodes, uiucCodeResults } = useArchiBusAPI()
                const { emplResults, fetchEmployeesOnOrgs, storeEmployee } = useEmployeesAPI()
                const { userResults, getOIDOnUserPrincipalName, getOIDOnUserDisplayName, graphError, statusCode } = useGraphAPI()
                const {checkAccessToken} = useAuthAPI()

                this.storableEmployees = []

                this.bEmployeesLoaded       =   false
                this.renderState            =   'IMPORTING'
                // since we need the information on colleges and departments, fetch from ArchiBus first
                await makeUIUCCodes()
                .then ( async () => {
                    this.uiucCodes = uiucCodeResults.value

                    // select list 
                    this.aCollegeCodes         =   this.uiucCodes['college-codes']
                    this.aDeptCodes            =   this.uiucCodes["dept-codes"]             // name[0], college-code[1], dept-code[2]
                    this.aOrgCodes             =   this.uiucCodes["org-codes"]    
                                     
                    return this.uiucCodes
                })
                .then( async () =>    {

                    await fetchEMEmployeesOnOrgs(this.aOrgs)
                    .then( async () =>    {
                        // iterate over returned employees, translating properties from ArchiBus form to internal 
                        // property names, then store employees

                        let resolvedPromises = await Promise.allSettled(employeeEMResults.value.map(async em => {
                            let storableEmpl = {
                                "ms-object-id":         "",
                                "first-name":           "",
                                "last-name":            "",
                                "middle-initial":       "",
                                "netid":                "",
                                "uin":                  "",
                                "image":                "",
                                "title":                "",
                                "home-org":             "",
                                "college":              "",
                                "college-code":         "",
                                "department":           "",                 
                                "department-code":      "",
                                "division":             "",
                                "division-code":        ""              
                            }
                            // pick up the directly translatable properties
                            // first-name is incorrectly entered in some em records
                            storableEmpl['last-name']       =   em['name_last']

                            //  attempt to get MI - may be last name only
                            let aNameParts = em.name.split(',')
                            if (typeof aNameParts[1] != 'undefined') {
                                let aFirstNameParts = aNameParts[1].trim().split(" ")
                                if(typeof aFirstNameParts[1] != 'undefined' ) {
                                    storableEmpl['middle-initial']   = aFirstNameParts[1]
                                }
                                if(typeof aFirstNameParts[0] != 'undefined' ) {
                                    // overwrite first name
                                    storableEmpl['first-name']   = aFirstNameParts[0]
                               }
                            }
                            let tempDisplayName = storableEmpl['last-name']+', ' + storableEmpl['first-name']
                            if(storableEmpl['middle-initial'].length != 0)
                                tempDisplayName += ' '+storableEmpl['middle-initial']

                            // other properties
                            storableEmpl['netid']           =   em['net_user_name']
                            storableEmpl['uin']             =   em['em_id']
                            storableEmpl['department-code'] =   em['dv_id']
                            storableEmpl['division-code']   =   em['dp_id']
                            storableEmpl['home-org']        =   em['org_id']

                            // lookup the college name, college-code, and division name on uiucCodes
                            let div = this.aOrgCodes.find( (d) => d.org == em['dp_id'])
                            if(typeof div != 'undefined')   {
                                storableEmpl['division']        =   div.name
                            }
                            else console.log("ListEmployees: importEMTable: listed division id (dp_id): "+em['dp_id']+" not found in UIUC codes")

                            let dept = this.aDeptCodes.find( (d) => d['dept-code'] == em['dv_id'])
                            if(typeof dept != 'undefined') {
                                storableEmpl['college-code']    =   dept['college-code']
                                storableEmpl['department']      =   dept['name']

                                // resolve college name
                                let col = this.aCollegeCodes.find( (c) => c.code == dept['college-code'])
                                if(typeof col != 'undefined') {
                                    storableEmpl['college']         =   col.name
                                }
                                else console.log("ListEmployees: importEMTable: cound not obtain college record in UIUC codes")
                            }
                            else console.log("ListEmployees: importEMTable: listed dept id (dv_id): "+em['dv_id']+" not found in UIUC codes")

                            // generate principal email
                            if(em.hasOwnProperty('net_user_name')  && em['net_user_name'] != null) {
                                storableEmpl['contacts']    =   []
                                let contactTemplate         =   {
                                    "type":     "email",
                                    "value":    em['net_user_name'] + "@illinois.edu"
                                }
                                storableEmpl['contacts'].push(contactTemplate)
                            }
                             
                            // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                            //
                            //      ACCESS GRAPH TO OBTAIN OID (needed for role management)
                            //      Note:  Some persons in Tesh Servies orgs have uillinois domains.
                            //
                            // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                            if(storableEmpl.netid  != null) {
                                let emplPrincipalName = storableEmpl.netid + "@illinois.edu"

                                await checkAccessToken()            // ensure gat not expired
                                // use principalName to get OID from graph
                                await getOIDOnUserPrincipalName(this.gat, emplPrincipalName)
                                .then( async ()  =>   {
                                    if(statusCode.value == 200) {
                                        // set ms-object-id on store
                                        storableEmpl['ms-object-id']  = userResults.value.id
                                        storableEmpl['title']         = this.prettyType(userResults.value.jobTitle)
                                        // obtain telephone contacts from graph also
                                        // array businessPhones: []   '1+ ' prefix internationalization
                                        let aPhones = userResults.value.businessPhones
                                        let pType = ""
                                        aPhones.forEach( (ph, num) =>   {
                                            if(num < 2) {
                                                switch(num) {case 0: pType = 'work telephone';break;
                                                            case 1: pType = 'alternate telephone'; break;
                                                            default: break;         
                                                }
                                                let phoneTemplate         =   {
                                                    "type":     pType,
                                                    "value":    ph.substr(3)
                                                }
                                                storableEmpl.contacts.push(phoneTemplate)
                                            }
                                        })
                                        return storableEmpl
                                    }
                                    else if(statusCode.value == 404) {
                            
                                        // not found on principalName
                                        // not found in AD - maybe due to uillinois domain
                                        // rewrite and retry
                                        emplPrincipalName = storableEmpl.netid + "@uillinois.edu"
                                        await checkAccessToken()
                                        await getOIDOnUserPrincipalName(this.gat, emplPrincipalName)
                                        .then( () =>    {
                                            // set ms-object-id on store
                                            storableEmpl['ms-object-id']  = userResults.value.id
                                            storableEmpl['title']         = this.prettyType(userResults.value.jobTitle)
                                            // overwrite the contacts
                                            storableEmpl['contacts']    =   []
                                            let contactTemplate         =   {
                                                "type":     "email",
                                                "value":    em['net_user_name'] + "@uillinois.edu"
                                            }
                                            storableEmpl['contacts'].push(contactTemplate)
                                            // obtain telephone contacts from graph also
                                            // array businessPhones: []   '1+ ' prefix internationalization
                                            let aPhones = userResults.value.businessPhones
                                            let pType = ""
                                            aPhones.forEach( (ph, num) =>   {
                                                if(num < 2) {
                                                    switch(num) {case 0: pType = 'work telephone';break;
                                                                case 1: pType = 'alternate telephone'; break;
                                                                default: break;         
                                                    }
                                                    let phoneTemplate         =   {
                                                        "type":     pType,
                                                        "value":    ph.substr(3)
                                                    }
                                                    storableEmpl.contacts.push(phoneTemplate)
                                                }
                                            })
                                            return storableEmpl
                                        })
                                    }
                                    else {  
                                        console.log("listEmployees:: importEM: getOIDOnUserPrincipalName: error: HTTP status: "+statusCode.value)
                                    }
                                })
                            }
                            else {
                                // no netid so likely no email and therefore no principal name
                                // attempt to use display name - note this will not correct email domain
                                await checkAccessToken()
                                await getOIDOnUserDisplayName(this.gat, tempDisplayName)
                                .then( ()  =>   {
                                    // set ms-object-id on store
                                    storableEmpl['ms-object-id']  = userResults.value.id
                                    storableEmpl['title']         = this.prettyType(userResults.value.jobTitle)
                                    return storableEmpl
                                })
                                .catch( () => {
                                    this.bGraphError = graphError.value
                                })
                            } 
                            return storableEmpl   // this becomes rp.value
                         }))
                        
                        // have an array of settled promised with either resolve or response callbacks
                        resolvedPromises.forEach((rp, num)  =>  {

                            if(rp.status == 'rejected') {
                                //throw Error (Promise rejected)
                            }
                            else if(rp.status == 'fulfilled')  {
                                // data available on promise    
                                this.storableEmployees.push(rp.value)
                            }
                        })
                        return this.storableEmployees
                    })
                    .then ( async() => {
              
                        await storeEmployee( this.storableEmployees) 
                        .then ( async () => {
                            await fetchEmployeesOnOrgs(this.aOrgs)
                            .then( () =>    {
                                this.employees = emplResults.value      
                                this.renderState          =   'LIST'
                                this.bEmployeesLoaded     =   true
                                
                            })
                        })
                    })
                })
            },
            prettyType(s) {
                if (!s) return ""
                s = s.replaceAll('_',' ')
                let aW = s.toLowerCase().split(' ')
                for (let i = 0; i < aW.length; i++) {
                    aW[i] = aW[i].charAt(0).toUpperCase() + aW[i].slice(1);
                }
                return aW.join(' ')
            },
            // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
            // Data Export Processes
            // Button triggers render switch to EXPORT rendering
            // Option buttons are presented
            // User clicking option button will generate file and download to braowser 'downloads'
            // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>> 
            exportEmployees()  {
                //console.log("LE:: Export employees fired")

                this.renderState = 'EXPORT'
            },
            exportXLSX()    {
                // check for empty filteredlist
                if(this.filteredEmployees.length == 0)  {
                    this.renderState = 'LIST'
                    return
                }
                const workbook  =   XLSX.utils.book_new()

                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                //
                //          SECTION TO GENERATE SPREADSHEET CELL HEADER VALUES
                //
                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>

                // header generator Booleans - will be set true when found, otherwise will be excluded
                // from set reporting
                let bHaveEmployeeHeaders   = false
                let bHaveContactHeaders    = false
                
                // set up headers - pick up the first employee [0] and strip headers
                // we cannot assume all employee have subordinate records - we need to find a record with
                // a contact list
                //
                // iterate the employees to extract headers
                let aEmployeeHeaders    =   []      // array of header keys
                let aContactHeaders     =   []
                
                this.filteredEmployees.forEach( (a) =>    {
                    if(!bHaveEmployeeHeaders) {
                        aEmployeeHeaders = Object.keys(a ?? {}) 
                        if(aEmployeeHeaders.length > 2 )   {
                            bHaveEmployeeHeaders = true
                        }
                    }
                    if(!bHaveContactHeaders) {
                        let aContactItemsKeys = Object.keys(a?.['contacts'] ?? [])
                        aContactItemsKeys.forEach( (s) =>  {
                            if(!bHaveContactHeaders) {
                                aContactHeaders = Object.keys(a?.['contacts']?.[s] ?? {})
                                if(aContactHeaders.length > 2 )   {
                                    bHaveContactHeaders = true
                                }
                            }
                        })
                    }  
                })
             
                // MARK: - Remove array items from headers
                let propIndex = -1
                propIndex = aEmployeeHeaders.indexOf('contacts')
                if(propIndex != -1) aEmployeeHeaders.splice(propIndex,1)

                // treat allowed-orgs as string by concatenating array elements on employee record
                
                // MARK: - Remove database specific FKs
                propIndex = aContactHeaders.indexOf('fk-employee-id')
                if(propIndex != -1) aContactHeaders.splice(propIndex,1)
                                  
                // MARK: - Check export format - if individual, insert headers for FA to associate records
                //         Use netid as FA
                if(this.sExportFormat == 'individual')   {
                    // individual spreadsheet reporting - add foreign attributes (FA) as FK for association
                    if(bHaveContactHeaders)    aContactHeaders.unshift('netid')
                }

                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                //
                //          MAKE META RECORDS AT TOP OF SPREADSHEET
                //
                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                var currentSheet    =   []             //   array or rows
                var currentRow      =   []             //   array of cells
                let cRow            =   0              //   indexed to 0 in 'xlsx-js-style'
                let hdrRows         =   []             // emphasized rows (headers)
                let boldRows        =   []             // bolded data rows for agreement data (easier to differentiate)
                let sectionHeads    =   []             // + components

                // generate date of report
                const date = new Date()
                let year    =   date.getFullYear().toString()
                let month   =   date.getMonth()+1
                month       =   month.toString().padStart(2,'0')
                let day     =   date.getDate()
                day         =   day.toString().padStart(2,'0')
                let hours   =   date.getHours().toString().padStart(2,'0')
                let mins    =   date.getMinutes().toString().padStart(2,'0')

                currentRow.push("Exported Employees")
                currentSheet.push(currentRow); cRow++
                currentRow = []
                currentRow.push(" ")
                currentRow = []
                currentRow.push("Date:")
                currentRow.push(month+'/'+day+'/'+year)
                currentSheet.push(currentRow); cRow++
                currentRow = []
                currentRow.push("Time:")
                let time = (hours >= 12)?(hours-12+":"+mins+" PM"):(hours+":"+mins+" AM")
                currentRow.push(time)
                currentSheet.push(currentRow); cRow++
                currentRow = []
                currentRow.push("Created by:")
                currentRow.push(this.user['name-concat'])
                currentSheet.push(currentRow); cRow++
                currentRow = []
                currentRow.push(" ")
                currentSheet.push(currentRow); cRow++
                currentSheet.push(currentRow); cRow++
                // end of meta data
                
                // create employee header row
                currentRow = []
                aEmployeeHeaders.forEach( (hdr) => {
                    currentRow.push(hdr)
                })
                // bold the row following (pre-increment cRow)
                hdrRows.push(cRow)
                currentSheet.push(currentRow); cRow++


                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                //
                //          ITERATE FILTERED AGREEMENTS CONVERTING AGREEMENTS TO SHEET ROWS
                //
                //          If 'consolidated' reporting, intersperse array prop records
                //          else just do agreements and iterate filteredAgreements again later
                //          for each array prop type as separate sheet/tab
                //          Use:
                //              Generates array of arrays (array of rows)
                //              XLSX.utils.aoa_to_sheet(aoa) for data
                //              XLSX.utils.book_append_sheet for each  of secheules, remote-assets and 
                //              comm-methods
                //
                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                currentRow = []
                this.filteredEmployees.forEach( (_,i) => {
                    aEmployeeHeaders.forEach((hdr) =>    {
                        // report allowed orgs as comma delimited string
                        if(hdr == 'allowed-orgs')   {
                            let aorgs = this.filteredEmployees[i]?.[hdr].join(", ")
                            currentRow.push(aorgs)
                        }
                        else currentRow.push(this.filteredEmployees[i]?.[hdr])
                    })
                    // conditionally format on consolidated sheets
                    if(this.bIncludeArrayProps && this.sExportFormat == 'consolidated') boldRows.push(cRow)
                    currentSheet.push(currentRow); cRow++
                    currentRow = []

                    // check for array props (if consolidated format)
                    if(this.bIncludeArrayProps && this.sExportFormat == 'consolidated') {
                        // check schedules
                        if(this.filteredEmployees[i].hasOwnProperty('contacts')) {
                            if(this.filteredEmployees[i]['contacts'].length > 0)  {
                                // render contacts marker
                                currentRow.push("+")
                                currentRow.push("Contacts")
                                sectionHeads.push(cRow)
                                currentSheet.push(currentRow); cRow++
                                currentRow = []
                                // render contact headers
                                aContactHeaders.forEach( (hdr) => {
                                    currentRow.push(hdr)
                                })
                                // bold the row following (pre-increment cRow)
                                hdrRows.push(cRow)
                                currentSheet.push(currentRow); cRow++
                                currentRow = []
                                // now iterate the contacts
                                this.filteredEmployees[i]['contacts'].forEach( (c) => {
                                    aContactHeaders.forEach((hdr) =>    {
                                        currentRow.push(c[hdr])
                                    })
                                    currentSheet.push(currentRow); cRow++
                                    currentRow = []
                                })
                            }
                        }

                        // finished with all subordinate array props so return to agreement row processing
                        currentRow.push("-")
                        sectionHeads.push(cRow)
                        currentSheet.push(currentRow); cRow++
                        currentRow = []
                    }
                    // continue iterating employees
                })

                // build the workbook with current data    
                var worksheet = XLSX.utils.aoa_to_sheet(currentSheet)

                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                //  
                //          APPLY FORMATTING TO SHEET
                //
                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                // format primary 'Employees' tab
                // !cols is an array of column format data used in xlsx-js-style - presumed objects {}
                // the maximum number of columns is the maximum length of the length of the headers array
                const sheetColumns = Math.max(aEmployeeHeaders.length, aContactHeaders.length)
                // maximum column width is established because of text entries in employees.  Data must be
                // folded.  Minimum width provides for spacing for " " entries
                const maxWidth = 40
                const minWidth = 5

                // initialize style array to minWidth
                let aColStyles = []
                for (let i = 0; i < sheetColumns; i++) {
                    let oColArg    =    {
                        wch: minWidth
                    }
                    aColStyles.push(oColArg)
                }
                
                // iterate on array determined the maximum width of a column for cRow rows.
                for (let i = 0; i < cRow; i++)  {
                    // how many elements in the ith row array of the sheet array
                    if(currentSheet?.[i]?.length >= 1)   {
                        // iterate over the elements measuring the length (width precursor)
                        for (let j = 0; j < currentSheet?.[i]?.length; j++)  {
                            let element = currentSheet?.[i]?.[j]
                            // if null record coalesce to default width
                            let elWidth = element?.length ?? minWidth
                            // restrict cell to maximum width
                            elWidth = (elWidth > maxWidth)?maxWidth:elWidth
                            aColStyles[j].wch = Math.max(elWidth, aColStyles[j].wch)  
                        }
                    }
                }

                // MARK: Apply format of column width
                worksheet["!cols"] = aColStyles

                // run cell styler
                for (let i in worksheet) {
                    if (typeof(worksheet[i]) != "object") continue;
                    let cell = XLSX.utils.decode_cell(i);

                    // MARK: set global styles
                    worksheet[i].s = {
                        font:   {
                            sz:     "10",
                            color:  {rgb: "000000"}
                        },
                        alignment:  {
                            horizontal:     "left",
                            wrapText:       true
                        }
                    }
                    if(cell.r == 0) {
                        // first row
                        worksheet[i].s = {
                            font:   {
                                sz:     "12",
                                color:  {rgb: "0000FF"},
                                bold: true
                            },
                        }
                    }
                    if(hdrRows.includes(cell.r))   {
                        // header rows highlighted
                        worksheet[i].s = {
                            //fill: {
                            //    patternType:    "solid",
                            //    bgColor:        {rgb: "090909"}      
                            //},
                            font: {
                                sz:             "11",
                                bold:           true,
                                color:          {rgb: "0000FF"}
                            }
                        }
                    }
                    if(boldRows.includes(cell.r))   {
                        // agreement data bolded
                        worksheet[i].s = {
                            font: {
                                bold:           true
                            }
                        }
                    }
                    if(sectionHeads.includes(cell.r)) {
                        worksheet[i].s = {
                            font:   {
                                sz:     "11",
                                color:  {rgb: "999900"},
                                bold: true
                            }
                        }
                    }
                }
 
                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                //
                //          CONSTRUCT THE XLSX WORKBOOK 
                //
                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                XLSX.utils.book_append_sheet(workbook, worksheet, 'Employees');

                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                //
                //          IF INDIVIDUALIZED FORMAT APPEND INDIVIDUAL WORKSHEETS TO WORKBOOK
                //
                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                //MARK:  Now see if user wants array props
                if(this.bIncludeArrayProps && this.sExportFormat == 'individual') {

                    var currentContactSheet    =   []             //   array of rows
                    let cContactRow            =   0             //   indexed to 0 in 'xlsx-js-style'                           
                    let contactHdrRows         =   []
                   
                    // build the individualized worksheet headers
                    if(aContactHeaders.length > 0) {
                        currentRow = []
                        // construct header row
                        aContactHeaders.forEach( (hdr) => {
                            currentRow.push(hdr)
                        })
                        // bold the row following (pre-increment cxRow)
                        contactHdrRows.push(cContactRow)
                        currentContactSheet.push(currentRow); cContactRow++
                    }

                    // MARK:  Now iterate the set of filtered Employees extracting
                    //        contact info     
                    //
                    this.filteredEmployees.forEach( (_,i) => {
                        // sometimes employees have no array properties - guard against this
                        if(this.filteredEmployees[i].hasOwnProperty('contacts')) {
                            if(this.filteredEmployees[i]['contacts'].length > 0)  {
                                
                                // iterate the contacts on the employee
                                this.filteredEmployees[i]['contacts'].forEach( (c) => {
                                    currentRow = []
                                    // first header is FA as association key to employee (netid)
                                    currentRow.push(this.filteredEmployees[i]['netid'])
                                    // skip 0th element (number)
                                    for (let j=1; j < aContactHeaders.length; j++) {
                                        currentRow.push(c[aContactHeaders[j]])
                                    }
                                    currentContactSheet.push(currentRow); cContactRow++
                                })
                            }
                        }
                        // continue iterating employees
                    })  

                    // MARK: Create the auxillary worksheet
                    var worksheetContacts   = XLSX.utils.aoa_to_sheet(currentContactSheet)
                 
                    // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                    //  
                    //          APPLY FORMATTING TO AUXILARY SHEETS
                    //              CONTACTS TAB
                    //
                    // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                    // MARK:  Do the width array setup
                    // count of contact headers 
                    const contactHdrs = aContactHeaders?.length 
                    // initialize style array to minWidth
                    let aContactColStyles = []
                    for (let i = 0; i < contactHdrs; i++) {
                        let oColArg    =    {
                            wch: minWidth
                        }
                        aContactColStyles.push(oColArg)
                    }
                  
                    // MARK: - Run determination of maximum column width for tab
                    
                    // iterate on array and determine the maximum width of a column for number of rows.
                    for (let i = 0; i < cContactRow; i++)  {
                        // how many elements in the ith row array of the sheet array
                        if(currentContactSheet?.[i]?.length >= 1)   {
                            // iterate over the elements measuring the length (width precursor)
                            for (let j = 0; j < currentContactSheet?.[i]?.length; j++)  {
                                let element = currentContactSheet?.[i]?.[j]
                                // if null record coalesce to default width
                                let elWidth = element?.length ?? minWidth
                                // restrict cell to maximum width
                                elWidth = (elWidth > maxWidth)?maxWidth:elWidth
                                aContactColStyles[j].wch = Math.max(elWidth, aContactColStyles[j].wch)  
                            }
                        }
                    }

                    // MARK: Apply format of column width to all sheets
                    worksheetContacts["!cols"] = aContactColStyles

                    // apply on a per-cell basis the rest of the formatting
                    for (let i in worksheetContacts) {
                        if (typeof(worksheetContacts[i]) != "object") continue;
                        let cell = XLSX.utils.decode_cell(i);

                        // MARK: set global styles
                        worksheetContacts[i].s = {
                            font:   {
                                sz:     "10",
                                color:  {rgb: "000000"}
                            },
                            alignment:  {
                                horizontal:     "left",
                                wrapText:       true
                            }
                        }
                        if(contactHdrRows.includes(cell.r))   {
                            // header rows highlighted
                            worksheetContacts[i].s = {
                                font: {
                                    sz:             "11",
                                    bold:           true,
                                    color:          {rgb: "0000FF"}
                                }
                            }
                        }
                    }

                    // MARK:  Append worksheets to workbook
                    XLSX.utils.book_append_sheet(workbook, worksheetContacts, 'Contacts');
                }   

                // 20240404_1559 format
                const filedate = year+month+day+'_'+hours+mins
                this.filename = "employees"+"-"+filedate+".xlsx"    

                XLSX.writeFile(workbook, this.filename, { compression: true, cellStyles: true });

                this.renderState = 'LIST'
                return
            },
            exportJSON() {

                // check for empty filteredlist
                if(this.filteredEmployees.length == 0)  {
                    this.renderState = 'LIST'
                    return
                }
                
                // convert employees to JSON datafile
                this.datafile = JSON.stringify(this.filteredEmployees)

                var blobData = []
                blobData.push(this.datafile)

                const date = new Date()
                let year    =   date.getFullYear().toString()
                let month   =   date.getMonth()+1
                month       =   month.toString().padStart(2,'0')
                let day     =   date.getDate()
                day         =   day.toString().padStart(2,'0')
                let hours   =   date.getHours().toString().padStart(2,'0')
                let mins    =   date.getMinutes().toString().padStart(2,'0')

                // 20240404_1559 format
                const filedate = year+month+day+'_'+hours+mins
                this.filename = "employees"+"-"+filedate+".json"    

                var textFileAsBlob =  new Blob(blobData, {type:'application/json'});
                //var saveAsFilename = document.getElementById("_saveAs").value;

                var downloadLink = document.createElement("a")
                downloadLink.setAttribute("id","_pseudoID")
                
                downloadLink.download = this.filename
                downloadLink.innerHTML = "_"
                if (window.webkitURL != null) {
                    // Chrome allows the link to be clicked
                    // without actually adding it to the DOM.
                    downloadLink.href = window.webkitURL.createObjectURL(textFileAsBlob)
                }
                else  {
                    // Firefox requires the link to be added to the DOM
                    // before it can be clicked.
                    downloadLink.href = window.URL.createObjectURL(textFileAsBlob)
                    downloadLink.onclick = document.body.removeChild(target)
                    downloadLink.style.display = "none"
                    document.body.appendChild(downloadLink)
                }
                downloadLink.click()
                window.URL.revokeObjectURL(textFileAsBlob)
                downloadLink.remove()

                // back to the barn
                this.renderState = 'LIST'
            },
            
            setFilters() {

                // deprecated for computed proxy in setup

                //console.log("LE:: setFilters:  method fired")
                //console.log("LE:: setFilters: filters: "+JSON.stringify(this.filters))

                //console.log("LE:: setFilters: filteredEmployees: "+JSON.stringify(this.filteredEmployees))

            },
            clearFilters()  {

                //console.log("LE:: clearFilters:  method fired")

                // reset the filters themselves
                this.filters =  {
                    'name':             "",
                    'netid':            "",
                    'uin':              "",
                    "home-org":         "",
                    "state":            "",
               }
            },
            // FUNCTION: Invoked from contact entry form as emit on filteredEmployees[viewIndex]
            // Argument aContact is the contact object added to employee prop on ViewEditEmployeeEntire
            // Note that this changes the OBJECT not the stored instance.  When employee is stored under SAVE
            // the modified contacts array is written to database
            addContactToEmployee(empl, contact) {
                empl.contacts.push(contact)
            },
            setEmployeeAffiliation(_, codeSelection) {

                this.mutatableEmployee['college']           = codeSelection['college']
                this.mutatableEmployee['college-code']      = codeSelection['college-code']
                this.mutatableEmployee['department']        = codeSelection['department']
                this.mutatableEmployee['department-code']   = codeSelection['dept-code']
                this.mutatableEmployee['division']          = codeSelection['div/org']
                this.mutatableEmployee['division-code']     = codeSelection['orgcode']

            },

            // FUNCTION: Invoked from main contacts list (x) as emit
            // Argument is index into contacts array on employee prop filteredEmployees[viewIndex]
            // removes element from .contacts array
            deleteContactFromEmployee(empl, idx) {
                 empl.contacts.splice(idx,1)
            },
            // maybe deprecated
            setEmplAllowedOrgs(sOrgs)   {
                this.mutatableEmployee['allowed-orgs'] = sOrgs.split(',')
            },
            setEmployeeOID(oid) {
                // sets OID on employee object
                this.mutatableEmployee['ms-object-id'] = oid
                this.saveEmployee()       
            },
            setEmplDeptAdminPrivilege (b, oid)   {
                // security group '2024-RWA-Dept-Admin'
                const { addMemberToGroup, removeMemberFromGroup } = useGraphAPI()
                const {checkAccessToken } = useAuthAPI()

                checkAccessToken()
                let deptAdminId = this.AdminGroups.find ((gp) => {
                    if(gp.role == "Dept_Admin") return gp
                }).id
                if(b) {
                    addMemberToGroup(this.gat, oid, deptAdminId)
                }
                else {
                    removeMemberFromGroup(this.gat, oid, deptAdminId)
                }
            },
            setEmplSysAdminPrivilege(b, oid)  {
                // b is Boolean flag generated by vmodel on check box
                // security group '2024-RWa-Sys-Admin'
                const { addMemberToGroup, removeMemberFromGroup} = useGraphAPI()
                const {checkAccessToken } = useAuthAPI()

                checkAccessToken()

                // if employee as selected employee is self, and already is system Admin, 
                // disallow self deprecation of privilege
                if((this.employees[this.viewIndex].uuid == this.user.uuid )
                    && this.user['is-sys-admin'])   {
                    // self- already is sys admin or couldn't see form in first place
                    console.log("ListEmployees:: setEmplSysAdminPrivilege: User attempting to change self - disallowed")
                    return
                }               
                let sysAdminId = this.AdminGroups.find ((gp) => {
                    if(gp.role == "System_Admin") return gp
                }).id

                if(b) {
                    addMemberToGroup(this.gat, oid, sysAdminId)
                }
                else {
                    removeMemberFromGroup(this.gat, oid, sysAdminId)
                }
            },
            // FUNCTION: Created a PDF of ViewEditEmployee rendering
            // id of element on DOM is '_pdf_render'
            // Need to created from the DOM and then do a standard download so that the PDF appears
            // in the browser downloads.
            async generatePDF() {
                
                // MARK:  generate the appropriate filename with embedded date
                const date = new Date()
                let year    =   date.getFullYear().toString()
                let month   =   date.getMonth()+1
                month       =   month.toString().padStart(2,'0')
                let day     =   date.getDate()
                day         =   day.toString().padStart(2,'0')
                let hours   =   date.getHours().toString().padStart(2,'0')
                let mins    =   date.getMinutes().toString().padStart(2,'0')

                 // 20240404_1559 format
                 const filedate = year+month+day+'_'+hours+mins
                 let empl = this.mutatableEmployee['name-concat']
                
                // MARK: - Since this is a list of multiple employees, we want to 
                // identify the agreement document with the employee's last name in
                // addition to the agreement number.  This is available in string form and we
                // want only the last name
                let aNames = empl.split(" ")
                let lName = aNames[aNames.length-1]
                const pdfFileName = lName+"-Employee-Record-"+filedate+".pdf"    

                // select and the container and add a title for teh PDF document              
                var element = document.getElementById('_pdf_render');
                var title   = document.createElement("p")
                var brk     = document.createElement("br")
                title.textContent = "Employee Record"
                title.style['font-family'] = "'Times New Roman',Times, serif"
                title.style['font-size'] = "16pt"
                title.style['font-weight'] = "bold"
                title.style['text-align'] = "center"
                title.appendChild(brk)
                title.appendChild(brk)
                var firstChild = document.getElementById("_pdf_row1")
                element.insertBefore(title,firstChild)

                var options = {
                    filename: pdfFileName,
                    excludeTagNames: ['button']
                };
                await domToPdf(element, options, function(pdf) {
                     
                    title.remove()
                });
                    
            }

          
        },      // end of methods list


        // hooks
        beforeMount() {

        },
        mounted() {
            //console.log("LE Mounted:: Number of employees Found: "+this.employees.length)
            //console.log("LE Mounted:: Employeess: "+JSON.stringify(this.employees))
        },
        updated() {
            //console.log("LE:: Updated: user: "+JSON.stringify(this.user))
            //console.log("LE Update:: Number of employees found: "+this.employees.length)
            //console.log("LE Update:: Employeess: "+JSON.stringify(this.employees))

        }
    }
</script>

<style scoped>

    ._master_container {
        font-family:Monserrat, Avenir, Helvetica, Arial, sans-serif;
        min-height: 50vh;
        margin: 25px 0 0 0;
        padding: 0;
        overflow-x: auto;
    }
    ._export_container {
        width: 98%;
        overflow-x: auto;
        margin: 0;
        padding: 0;
    }
    ._spinner {
        min-height: 40vh;
        width: 40vw;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 45px auto;
        text-align: center;
    }

    ._list_container {
        width: 98%;
        overflow-x: auto;
        overflow-y: auto;
        margin: 0 5px;;
        padding: 0 5px;
        border-style: solid;
        border-width: 2px;
        border-color: #13294B;
        background-color: white;
    }
    ._list {
        margin: 0;
        padding: 0;
    }
    ._p_title_header {
        margin: 0 0 10px;
        padding: 2px 5px;
        color: #13294B;
        font-size: 10pt;
        font-weight: bold;
        text-align: center;
    }
    ._p_list_header {
        margin: 0 0 10px;
        padding: 2px 5px;
        color: #13294B;
        font-size: 10pt;
        font-weight: bold;
        text-align: left;
    }
    ._p_export {
        width: 50%;
        margin: 25px auto;
        padding: 2px;
        color:#006230;
        font-size: 1.0em;
        font-weight: normal;
        text-align: justify;
    }
    ._p_import {
        width: 50%;
        margin: 25px auto;
        padding: 2px;
        color:#006230;
        font-size: 1.0em;
        font-weight: normal;
        text-align: justify;
    }
    ._p_patience {
        width: 30%;
        font-size: 1em;
        color: #13294B;
        text-align: center;
        padding: 2px;
        margin: 2px auto;
    }
    ._p_import_center {
        width: 50%;
        margin: 25px auto;
        padding: 2px;
        color:#006230;
        font-size: 1.0em;
        font-weight: normal;
        text-align: justify;
    }
    ._p_import_right {
        width: 100%;
        margin: 0;
        padding: 5px;
        color:#006230;
        font-size: 1.0em;
        font-weight: normal;
        text-align: center;
    }
    ._file_selector {  
        margin: 25px auto;
        text-align: center;
        background-color: #13294B;
        border-style: solid;
        border-color: #13294B;
        border-width: 1px;
        border-radius: 4px;
        color: white;
        font-size: 1.0em;
        font-weight: normal;
    }

    /* list action button styling */
    ._action_button {
        border-style: solid;
        border-color: #13294B;
        border-width: 1px;
        border-radius: 4px;
        background-color:#13294B;
        color: white;
        margin: 0 5px;
        padding: 0 5px;
        font-size: 0.8em;
    }
    ._action_button:hover {
        background-color: #FF5F05;
        color: white;
    }
    ._action_button_remove {
        border-style: solid;
        border-color: #13294B;
        border-width: 1px;
        border-radius: 4px;
        background-color:red;
        color: white;
        margin: 0 5px;
        padding: 0 5px;
        font-size: 0.8em;
    }
    ._action_button_remove:hover {
        background-color: white;
        color: red;
    }
   
    ._employees_controls {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        margin: 0;
        padding: 0 5px;;
    }
    ._employees_inline_controls {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        margin: 0;
        padding: 0 5px;
    }
    ._export_check {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 2px;       
    }
    ._export_rules {
        margin: 0;
        padding: 2px 5px;
    }
    ._check {    
        margin: 0;
        padding: 0 6px;
    }
    ._check_label {
        margin: 0;
        padding: 0 6px;
    }
    /* employees list styling */
    ._scrollable_list_container {
        width: 100%;
        height: 500px;
        overflow: auto;
        margin: 0;
        padding: 0;
        border-style: solid;
        border-width: 2px;
        border-color: #13294B;
    }

    ._employees_table {
        display: table;
        width: 100%;
    }

    ._employees_table_row_fixed   {
        display: table-row;
        position: sticky !important;
        background-color:#BFC9D9;
        min-width: 40px;
        font-size: 0.8em;
        font-weight: normal;
        color: #13294B;
        height: 25px;
        white-space: nowrap;
        z-index: 1;
    }
  
    ._employees_table_row   {
        background-color:#a4b9dc;        
        display: table-row;
        min-width: 40px;
        font-size: 0.8em;
        font-weight: normal;
        color:  #13294B;
        height: 25px;
        white-space: nowrap;
    }
    ._employees_table_row:nth-child(2n+2) {
        background-color:#BFC9D9;
    }
    ._employees_table_cell  {
        display: table-cell;
        min-width: 10%;
        text-align: left;
        margin: 0;
        padding: 2px 10px 2px 30px;
    }
    ._employees_table_cell_ellipsis {
        /* display: table-cell; */
        max-width: 80%;
        text-overflow: ellipsis;
        white-space: nowrap;        
        text-align: left;
        margin: 0;
        padding: 2px 10px 2px 10px;
        
    }
    ._employees_actions_table_cell {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: justify;
        margin: 0 auto;
    }
    ._employees_table_link_cell {
        display: table-cell;
        min-width: 40px;
        font-weight: normal;
        text-align: left;
        color:#13294B;
        margin: 0;
        padding: 2px 0 2px 10px; 
    }
    ._employees_table_link_cell:hover {       
        color: #FF5F05; 
        font-weight: bold;   
    }
    ._no_employees_warning_container {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        padding: 20px;
        margin: 10px 0;
    }
    ._employees_warning {
        color: #FF5F05;
    }
    _._employees_warning_clear {
        float: right;
    }   
    ._input_filter {
        width: 70%;
        background-color: white;
        color: black;
        font-size: 0.9em;
        font-weight: normal;
        border-style: solid;
        border-radius: 3px;
        border-width: 1px;
        border-color: #13294B;
        margin: 0 3px 0;
        padding: 2px;
    }
 
    /* general controls styling */
    ._main_button {
        background-color: #13294B;
        color: white;
        font-weight: bold;
        border-style: solid;
        border-width: 1px;
        border-radius: 4px;
        border-color: #13294B;
        margin: 5px 10px;
        padding: 5px;
    }
    ._main_button:hover {
        background-color: #FF5F05;
        color: white;
    }
    ._button_clear_filters {
        background-color:#13294B;
        color: white;
        font-size: 0.9em;
        font-weight: bold;
        border-style: solid;
        border-radius: 5px;
        border-width: 1px;
        border-radius: 4px;
        border-color: #13294B;
        margin: 0 5px;
        padding: 0 5px;
    }
    ._button_clear_filters:hover  {
        background-color: #FF5F05;
        color: white;
    }   
    ._p_saved {
        font-size: 16pt;
        color: #FF5F05;
        padding: 2px;
    }
    ._saved_right {
        padding: 0;
        margin: 0 10px 0 0;
        float: right;
    }
    ._saved_center {
        padding: 0;
        margin: 3px 0;
        text-align: center;
    }
    ._import_fieldset {
        width: 100%;
        display: flex;
        flex-direction: column;
        flex-wrap: nowrap;
        justify-content: flex-start;
        margin: 0;
        padding: 0;
    }
    ._row {
        width: 70%;
        margin: 0 auto;
        text-align: center;
    }
    ._radio {
        display: inline-block;
        width: 16px;
        margin: 0;
        /* position: relative; 
        top: 2px; 
        */
    }
    ._item_label {
        display: inline-block;
        width: 100%; 
        font-size: 0.75em;
        font-weight: bold;
        color: #13294B;
        text-align: left;
        margin: 0;
        padding: 0 0 0 10px;
    }
    ._col_30 {
        display: inline-block;
        width: 30%;
        margin: 0;
        padding: 0;
    }
    ._col_33 {
        display: inline-block;
        width: 33%;
        margin: 0;
        padding: 0;
    }
    ._col_67 {
        display: inline-block;
        width: 67%;
        margin: 0 auto;
        padding: 0;
    }
    ._col_70 {
        display: inline-block;
        width: 70%;
        margin: 0 auto;
        padding: 0;
    }
    /* sub-columns for radio buttons and labels */
    ._scol_15 {
        display: inline-block;
        width: 15%;
        margin: 0;
        padding: 0;
    }
    ._scol_25 {
        display: inline-block;
        width: 25%;
        margin: 0;
        padding: 0;
    }
    ._scol_80 {
        display: inline-block;
        text-align: left;
        width: 80%;
        margin: 0;
        padding: 0;
    }
    ._scol_85 {
        display: inline-block;
        text-align: left;
        width: 85%;
        margin: 0;
        padding: 0;
    }
     /* sub-columns for enclosing column radio buttons and labels */
    ._scol_33 {
        display: inline-block;
        text-align: left;
        width: 33%;
        margin: 0 auto;
        padding: 0;
    }
    @media screen and (max-width: 800px) {
        ._import_fieldset {
            flex-wrap: wrap;  
        }
        ._col_30 {
            width: 100%;
        }
        ._col_33 {
            width: 80%;
        }  
        ._col_67 {
            width: 100%;
        }
        ._col_70 {
            width: 80%;
            margin: 0 auto;
        }  
    }
    @media screen and (max-width: 620px) {
        ._col_33 {
            width: 100%;
            margin-top: 0;
        }
       ._scol_25 {
            width: 100%;
            text-align: center;
        }
        ._scol_33 {
            width: 100%;
            text-align: center;
        }

        ._col_67 {
            width: 50%;
            margin: 0 auto;
        }  
        
    }
</style>